import Autocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import TextField from '@mui/material/TextField'
import { getFieldErrorMessages } from 'global/helpers/form'
import { type ReactElement } from 'react'
import { Controller, type FieldValues } from 'react-hook-form'
import type { KNFormAutocompleteProps, KNOptionProps } from './types'

const defaultRenderOption = (props, option): ReactElement => (
  <li {...props} key={option.value}>
    {option.label}
  </li>
)

const defaultRenderValue = (value, getItemProps): ReactElement => (
  <Chip {...getItemProps()} variant="outlined" size="small" label={value.label} />
)

const KNFormAutocomplete = <T extends FieldValues>({
  name,
  control,
  rules,
  label,
  options,
  groupBy,
  size,
  sx,
  disabled,
  multiple,
  renderOption,
  loading,
  onInputChange,
}: KNFormAutocompleteProps<T>): ReactElement => {
  const getValueOption = (value): KNOptionProps => options.find((option) => option.value === value) ?? value

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { invalid, error }, formState }): ReactElement => (
        <FormControl fullWidth sx={sx} error={invalid}>
          <Autocomplete
            multiple={!!multiple}
            size={size}
            groupBy={groupBy}
            disabled={formState.isSubmitting || disabled}
            renderOption={renderOption ?? defaultRenderOption}
            renderValue={multiple ? defaultRenderValue : undefined}
            renderInput={(params): ReactElement =>
              <TextField
                {...params}
                label={label}
                error={invalid}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            }
            value={getValueOption(value)}
            onChange={(_event, newValue, _reason): void => {
              onChange(Array.isArray(newValue) ? newValue.map((item) => item.value) : newValue.value)
            }}
            onInputChange={onInputChange ? (_event, newInputValue, reason) => {
              onInputChange(newInputValue)
              const currentOption = getValueOption(value)
              if (reason === 'input' && value && newInputValue !== currentOption.label) {
                onChange(null)
              }
            }: undefined}
            filterOptions={onInputChange ? (x) => x : undefined}
            options={options}
            disableClearable
            isOptionEqualToValue={(option, value): boolean => option.value === value.value}
          />
          {error?.message && <FormHelperText>{getFieldErrorMessages(error)}</FormHelperText>}
        </FormControl>
      )}
    />
  )
}

export default KNFormAutocomplete
